<template>
  <div class="bg-white rounded-lg shadow-md h-full flex flex-col">
    <!-- Header -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-securdia-orange/10 rounded-lg flex items-center justify-center">
            <svg class="w-4 h-4 text-securdia-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-bold text-secondary-800">{{ $t('sales_dashboard.product_distribution_title') }}</h3>
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.categories_and_manufacturers') }}</p>
          </div>
        </div>
        
        <!-- View toggle -->
        <div class="flex bg-secondary-100 rounded-lg p-1">
          <button 
            @click="switchView('category')"
            :class="viewType === 'category' ? 'bg-white text-securdia-blue shadow-sm' : 'text-secondary-600 hover:text-secondary-800'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          >
            {{ $t('sales_dashboard.categories') }}
          </button>
          <button 
            @click="switchView('manufacturer')"
            :class="viewType === 'manufacturer' ? 'bg-white text-securdia-blue shadow-sm' : 'text-secondary-600 hover:text-secondary-800'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          >
            {{ $t('sales_dashboard.manufacturers') }}
          </button>
        </div>
      </div>
    </div>

    <div class="p-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Pie Chart -->
        <div class="relative" style="height: 250px;">
          <pie-chart 
            v-if="chartDatasets.datasets[0].data.length > 0"
            :chart-data="chartDatasets"
            :options="chartOptions"
            :height="250"
          />
          <div v-else class="flex items-center justify-center h-full text-secondary-400 text-sm">
            {{ $t('sales_dashboard.no_data_available') }}
          </div>
        </div>

        <!-- Legend / List -->
        <div class="space-y-2 max-h-[250px] overflow-y-auto pr-2">
          <!-- Reset button when items are excluded -->
          <div v-if="excludedItems.length > 0" class="flex items-center justify-between mb-2 pb-2 border-b border-secondary-100">
            <span class="text-xs text-secondary-500">
              {{ excludedItems.length }} {{ excludedItems.length > 1 ? $t('sales_dashboard.hidden_count_plural') : $t('sales_dashboard.hidden_count') }}
            </span>
            <button 
              @click="resetExcluded"
              class="text-xs text-securdia-blue hover:text-securdia-dark-blue font-medium flex items-center gap-1"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ $t('sales_dashboard.show_all') }}
            </button>
          </div>
          
          <div 
            v-for="item in allCurrentData" 
            :key="item.name"
            @click="toggleItem(item.name)"
            :class="[
              'flex items-center gap-3 p-2 rounded-lg transition-all cursor-pointer select-none',
              isExcluded(item.name) 
                ? 'bg-secondary-100 opacity-50' 
                : 'hover:bg-secondary-50'
            ]"
            :title="isExcluded(item.name) ? $t('sales_dashboard.click_to_show') : $t('sales_dashboard.click_to_hide')"
          >
            <!-- Color indicator -->
            <div 
              class="w-4 h-4 rounded-full flex-shrink-0 transition-all"
              :style="{ 
                backgroundColor: isExcluded(item.name) ? '#9CA3AF' : getItemColor(item.name)
              }"
            >
              <!-- Strike-through icon for excluded -->
              <svg v-if="isExcluded(item.name)" class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            
            <!-- Name and bar -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between mb-1">
                <span :class="[
                  'text-sm font-medium truncate transition-all',
                  isExcluded(item.name) ? 'text-secondary-400 line-through' : 'text-secondary-700'
                ]">{{ getDisplayName(item) }}</span>
                <span :class="[
                  'text-sm font-bold ml-2 transition-all',
                  isExcluded(item.name) ? 'text-secondary-400' : 'text-secondary-800'
                ]">{{ item.count }} {{ $t('sales_dashboard.pieces_unit') }}</span>
              </div>
              <div class="h-1.5 bg-secondary-200 rounded-full overflow-hidden">
                <div 
                  class="h-full rounded-full transition-all"
                  :style="{ 
                    width: isExcluded(item.name) ? '0%' : (item.count / maxCountAll * 100) + '%',
                    backgroundColor: isExcluded(item.name) ? '#9CA3AF' : getItemColor(item.name)
                  }"
                ></div>
              </div>
            </div>
            
            <!-- Percentage -->
            <span :class="[
              'text-xs w-12 text-right transition-all',
              isExcluded(item.name) ? 'text-secondary-300' : 'text-secondary-500'
            ]">
              {{ isExcluded(item.name) ? '-' : ((item.count / totalCountAll) * 100).toFixed(1) + '%' }}
            </span>
          </div>

          <!-- Empty state -->
          <div v-if="allCurrentData.length === 0" class="text-center py-8 text-secondary-400 text-sm">
            {{ $t('sales_dashboard.no_data_available') }}
          </div>
        </div>
      </div>

      <!-- Summary stats -->
      <div class="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-secondary-100">
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-1">{{ viewType === 'category' ? $t('sales_dashboard.categories') : $t('sales_dashboard.manufacturers') }}</p>
          <p class="text-lg font-bold text-secondary-800">
            {{ currentData.length }}
            <span v-if="excludedItems.length > 0" class="text-sm font-normal text-secondary-400">
              / {{ allCurrentData.length }}
            </span>
          </p>
        </div>
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.total_products_label') }}</p>
          <p class="text-lg font-bold text-secondary-800">
            {{ totalCount }} st
            <span v-if="excludedItems.length > 0" class="text-sm font-normal text-secondary-400">
              / {{ totalCountAll }}
            </span>
          </p>
        </div>
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-1">{{ viewType === 'category' ? $t('sales_dashboard.largest_category') : $t('sales_dashboard.largest_manufacturer') }}</p>
          <p class="text-lg font-bold text-securdia-blue truncate">{{ topItem ? getDisplayName(topItem) : '-' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs';

// Create a reusable Doughnut chart component
const PieChart = {
  extends: Doughnut,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler() {
        this.$data._chart.destroy();
        this.renderChart(this.chartData, this.options);
      },
      deep: true
    }
  }
};

export default {
  name: 'ProductDistributionChart',
  components: {
    PieChart
  },
  props: {
    productStats: {
      type: Array,
      default: () => []
    },
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      viewType: 'category',
      excludedCategories: [],
      excludedManufacturers: [],
      chartColors: [
        '#1A8DE9', // Blå (Securdia)
        '#F29100', // Orange (Securdia)
        '#22C55E', // Grön
        '#8B5CF6', // Lila
        '#EC4899', // Rosa
        '#14B8A6', // Teal
        '#EF4444', // Röd
        '#FBBF24', // Gul
        '#6366F1', // Indigo
        '#84CC16', // Lime
        '#0EA5E9', // Sky blue
        '#D946EF', // Fuchsia
        '#78716C', // Stone/grå
        '#FB7185', // Ljusrosa
        '#2DD4BF'  // Cyan
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 55,
        legend: {
          display: false
        },
        tooltips: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleFontColor: '#374151',
          bodyFontColor: '#6B7280',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          titleFontFamily: "'Source Sans Pro', sans-serif",
          bodyFontFamily: "'Source Sans Pro', sans-serif",
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.data[tooltipItem.index];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              const label = data.labels[tooltipItem.index];
              return `${label}: ${value} st (${percentage}%)`;
            }
          }
        },
        animation: {
          animateRotate: true,
          animateScale: true
        }
      }
    };
  },
  computed: {
    // Normalize productStats to always be an array
    normalizedProductStats() {
      // New format: object with products array
      if (this.productStats?.products && Array.isArray(this.productStats.products)) {
        return this.productStats.products;
      }
      // Legacy format: direct array
      if (Array.isArray(this.productStats)) {
        return this.productStats;
      }
      // Fallback: try to extract product-like objects from values
      if (this.productStats && typeof this.productStats === 'object' && Object.keys(this.productStats).length > 0) {
        const values = Object.values(this.productStats).filter(p => p && typeof p === 'object' && p.name);
        if (values.length > 0) {
          return values;
        }
      }
      return [];
    },
    categoryData() {
      const categories = {};
      
      this.normalizedProductStats.forEach(product => {
        const cat = product.category || 'Okänd';
        if (!categories[cat]) {
          categories[cat] = { name: cat, count: 0, revenue: 0 };
        }
        categories[cat].count += product.unitsSold || 1;
        categories[cat].revenue += (product.totalRentalRevenue || 0) + (product.totalCashRevenue || 0);
      });
      
      // If no productStats, try to extract from contracts
      if (Object.keys(categories).length === 0 && this.contracts.length > 0) {
        this.contracts.forEach(contract => {
          const products = contract.products || [];
          products.forEach(product => {
            const cat = product.category || 'Okänd';
            if (!categories[cat]) {
              categories[cat] = { name: cat, count: 0, revenue: 0 };
            }
            categories[cat].count += product.quantity || 1;
            categories[cat].revenue += (product.price || 0) * (product.quantity || 1);
          });
        });
      }
      
      return Object.values(categories).sort((a, b) => b.count - a.count);
    },
    manufacturerData() {
      const manufacturers = {};
      
      // Use normalized array
      this.normalizedProductStats.forEach(product => {
        const mfr = product.manufacturer || 'Okänd';
        if (!manufacturers[mfr]) {
          manufacturers[mfr] = { name: mfr, count: 0, revenue: 0 };
        }
        manufacturers[mfr].count += product.unitsSold || 1;
        manufacturers[mfr].revenue += (product.totalRentalRevenue || 0) + (product.totalCashRevenue || 0);
      });
      
      // If no productStats, try to extract from contracts
      if (Object.keys(manufacturers).length === 0 && this.contracts.length > 0) {
        this.contracts.forEach(contract => {
          const products = contract.products || [];
          products.forEach(product => {
            const mfr = product.manufacturer || product.brand || 'Okänd';
            if (!manufacturers[mfr]) {
              manufacturers[mfr] = { name: mfr, count: 0, revenue: 0 };
            }
            manufacturers[mfr].count += product.quantity || 1;
            manufacturers[mfr].revenue += (product.price || 0) * (product.quantity || 1);
          });
        });
      }
      
      return Object.values(manufacturers).sort((a, b) => b.count - a.count);
    },
    currentData() {
      const allData = this.viewType === 'category' ? this.categoryData : this.manufacturerData;
      const excluded = this.excludedItems;
      return allData.filter(item => !excluded.includes(item.name));
    },
    // All data without filtering (for legend display)
    allCurrentData() {
      return this.viewType === 'category' ? this.categoryData : this.manufacturerData;
    },
    // Excluded items for current view
    excludedItems() {
      return this.viewType === 'category' ? this.excludedCategories : this.excludedManufacturers;
    },
    totalCount() {
      return this.currentData.reduce((sum, item) => sum + item.count, 0);
    },
    // Total count including all items (for percentage calculations in legend)
    totalCountAll() {
      return this.allCurrentData.reduce((sum, item) => sum + item.count, 0);
    },
    maxCount() {
      return Math.max(...this.currentData.map(item => item.count), 1);
    },
    // Max count for all items (for bar width in legend)
    maxCountAll() {
      return Math.max(...this.allCurrentData.map(item => item.count), 1);
    },
    topItem() {
      return this.currentData[0] || null;
    },
    // Map item names to their original colors (based on position in allCurrentData)
    colorMap() {
      const map = {};
      this.allCurrentData.forEach((item, index) => {
        map[item.name] = this.chartColors[index % this.chartColors.length];
      });
      return map;
    },
    chartDatasets() {
      const data = this.currentData.slice(0, 10); // Top 10
      // Use original colors from colorMap to maintain consistency
      const colors = data.map(d => this.colorMap[d.name]);
      
      // Translate labels for categories
      const labels = this.viewType === 'category' 
        ? data.map(d => this.translateCategory(d.name))
        : data.map(d => d.name);
      
      return {
        labels: labels,
        datasets: [{
          data: data.map(d => d.count),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverBorderWidth: 3
        }]
      };
    }
  },
  methods: {
    /**
     * Translate product category from database value to display name
     */
    translateCategory(category) {
      if (!category) return this.$t('sales_dashboard.category_unknown');

      const key = category.toLowerCase().replace(/[\s-]/g, '_');

      const translations = {
        'alarm': this.$t('sales_dashboard.category_alarm'),
        'personal_alarm': this.$t('sales_dashboard.category_alarm'),
        'ajax': this.$t('sales_dashboard.category_ajax'),
        'tag': this.$t('sales_dashboard.category_tag'),
        'display': this.$t('sales_dashboard.category_display'),
        'vehicle_tracker': this.$t('sales_dashboard.category_vehicle_tracker'),
        'software': this.$t('sales_dashboard.category_software'),
        'simcard': this.$t('sales_dashboard.category_simcard'),
        'accessory': this.$t('sales_dashboard.category_accessory'),
        'other': this.$t('sales_dashboard.category_other')
      };

      if (translations[key]) {
        return translations[key];
      }

      return category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ');
    },
    /**
     * Get display name for item (category or manufacturer)
     */
    getDisplayName(item) {
      if (this.viewType === 'category') {
        return this.translateCategory(item.name);
      }
      return item.name;
    },
    switchView(type) {
      this.viewType = type;
    },
    toggleItem(name) {
      if (this.viewType === 'category') {
        const index = this.excludedCategories.indexOf(name);
        if (index === -1) {
          this.excludedCategories.push(name);
        } else {
          this.excludedCategories.splice(index, 1);
        }
      } else {
        const index = this.excludedManufacturers.indexOf(name);
        if (index === -1) {
          this.excludedManufacturers.push(name);
        } else {
          this.excludedManufacturers.splice(index, 1);
        }
      }
    },
    isExcluded(name) {
      return this.excludedItems.includes(name);
    },
    getItemColor(name) {
      return this.colorMap[name] || this.chartColors[0];
    },
    resetExcluded() {
      if (this.viewType === 'category') {
        this.excludedCategories = [];
      } else {
        this.excludedManufacturers = [];
      }
    }
  }
};
</script>
